// The only place that knows Directus collection and field names.
// Components receive the teacher-facing shapes returned by mapLesson/mapBlock.

import { BLOCK_TYPE_LABELS, studyAreaLabel } from "./labels";

export const LESSON_COLLECTION = "Lesson_Sessions";
export const BLOCK_COLLECTION = "lesson_blocks";

export const LESSON_FIELDS = [
  "id",
  "title",
  "week",
  "sequence",
  "study_area",
  "estimated_minutes",
  "objectives",
  "teacher_notes",
  "curriculum_package.grade_label",
  "curriculum_package.subject_label",
];

export const LESSON_SORT = ["week", "sequence", "title", "id"];

export const BLOCK_FIELDS = [
  "id",
  "sort",
  "block_type",
  "title",
  "duration_minutes",
  "teacher_actions",
  "learner_actions",
  "assessment_notes",
  "resource_notes",
];

// Teacher-visible = the lesson itself is approved AND published.
export const eligibleLessonFilter = () => ({
  review_status: { _eq: "approved" },
  publication_status: { _eq: "published" },
});

export const blocksForLessonFilter = (lessonId) => ({
  lesson_session: { id: { _eq: lessonId }, ...eligibleLessonFilter() },
});

export const ACTIVITY_COLLECTION = "learner_activities";
export const ACTIVITY_FIELDS = [
  "id",
  "title",
  "sequence",
  "lesson_session",
  "lesson_block",
  "lesson_block.lesson_session",
];
export const ACTIVITY_SORT = ["sequence", "id"];

// Learner activities the teacher may release at this exact block. The paired
// lesson filters enforce the lightweight cross-session runtime invariant.
export const activitiesForBlockFilter = ({ lessonId, blockId }) => ({
  lesson_session: { _eq: lessonId },
  lesson_block: {
    id: { _eq: blockId },
    lesson_session: { id: { _eq: lessonId } },
  },
  status: { _eq: "approved" },
  active: { _eq: true },
});

export const mapLearnerActivities = (rows) =>
  rows.map((row) => ({ id: row.id, title: text(row.title) }));

export class ContractError extends Error {
  constructor(message) {
    super(message);
    this.name = "ContractError";
  }
}

const text = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const positiveInt = (value) =>
  Number.isInteger(value) && value > 0 ? value : null;

export const slugify = (value) =>
  (value || "lesson")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "lesson";

// A class says its grade as a code (`R`, `1` … `12`); a curriculum package
// says it as a label ("Grade 1", "Grade R"). There is no relation between the
// two, so the label is read strictly: anything else matches no class.
export const gradeCodeOf = (value) => {
  const match = /^\s*(?:grade|gr\.?)?\s*(r|\d{1,2})\s*$/i.exec(
    typeof value === "string" ? value : ""
  );
  if (!match) return null;
  const code = match[1].toUpperCase();
  return code === "R" ? code : String(Number(code));
};

export const subjectKeyOf = (subject) => slugify(subject || "other lessons");

export const mapLesson = (raw) => {
  const subject = text(raw.curriculum_package?.subject_label);
  const grade = text(raw.curriculum_package?.grade_label);
  return {
    id: raw.id,
    title: text(raw.title) || "Untitled lesson",
    week: positiveInt(raw.week),
    studyArea: studyAreaLabel(raw.study_area),
    minutes: positiveInt(raw.estimated_minutes),
    objectives: text(raw.objectives),
    preparation: text(raw.teacher_notes),
    grade,
    gradeCode: gradeCodeOf(grade),
    subject,
    subjectKey: subjectKeyOf(subject),
  };
};

// Keeps server order and gives each lesson a stable, readable URL segment.
export const mapLessons = (rows) => {
  const seen = new Map();
  return rows.map((raw) => {
    const lesson = mapLesson(raw);
    const base = slugify(lesson.title);
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    return { ...lesson, slug: count === 1 ? base : `${base}-${count}` };
  });
};

export const mapBlock = (raw) => ({
  id: raw.id,
  sort: raw.sort,
  kind: BLOCK_TYPE_LABELS[raw.block_type] || null,
  isObservation: raw.block_type === "assessment",
  isLearnerActivity: raw.block_type === "learner_activity",
  title: text(raw.title),
  minutes: positiveInt(raw.duration_minutes),
  teacherActions: text(raw.teacher_actions),
  learnerActions: text(raw.learner_actions),
  lookFor: text(raw.assessment_notes),
  resources: text(raw.resource_notes),
});

// Block order must come from a unique integer `sort`; never guess it.
export const mapBlocks = (rows) => {
  const sorts = rows.map((row) => row.sort);
  if (
    sorts.some((sort) => !Number.isInteger(sort)) ||
    new Set(sorts).size !== sorts.length
  ) {
    throw new ContractError("Lesson steps have no reliable order.");
  }
  return [...rows].sort((a, b) => a.sort - b.sort).map(mapBlock);
};
